<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import Multiselect from '@vueform/multiselect';
import { api } from '@/helpers/common/api';

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

const STATUS_OPCOES = [
  { value: '', label: 'Todos os status' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

const CORES_PADRAO = [
  '#dc3545', '#fd7e14', '#ffc107', '#198754', '#20c997',
  '#0dcaf0', '#0d6efd', '#6610f2', '#d63384', '#6c757d',
];

export default {
  name: 'KanbanFiltros',
  components: { Multiselect },
  data() {
    return {
      ufs: UFS,
      statusOpcoes: STATUS_OPCOES,
      etiquetas: [],
      debounceTimer: null,
      // Criação inline via slot #noresults do Multiselect
      termoBuscaEtiqueta: '',
      coresDisponiveis: CORES_PADRAO,
      corCriacaoRapida: CORES_PADRAO[0],
      salvandoNova: false,
    };
  },
  computed: {
    ...mapGetters('atendimento', ['filtros']),
    busca: {
      get() { return this.filtros.busca; },
      set(v) { this.atualizar('busca', v); },
    },
    status: {
      get() { return this.filtros.status; },
      set(v) { this.atualizar('status', v || ''); },
    },
    estados: {
      get() { return this.filtros.estados; },
      set(v) { this.atualizar('estados', v || []); },
    },
    rotulosIds: {
      get() { return this.filtros.rotulosIds; },
      set(v) { this.atualizar('rotulosIds', v || []); },
    },
    temFiltrosAtivos() {
      const f = this.filtros;
      return Boolean(f.busca || f.status || (f.estados || []).length || (f.rotulosIds || []).length);
    },
  },
  async mounted() {
    await this.fetchEtiquetas();
  },
  methods: {
    ...mapActions('atendimento', ['setFiltro', 'resetFiltros']),
    atualizar(campo, valor) {
      // Debounce só pra busca textual; selects atualizam imediato.
      if (campo === 'busca') {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.setFiltro({ campo, valor }), 250);
      } else {
        this.setFiltro({ campo, valor });
      }
    },
    async fetchEtiquetas() {
      try {
        // Mesmo padrão do ContatoGrid — api.read faz unwrap do envelope.
        const resultado = await api.read('/rotulo?limit=9999');
        const lista = Array.isArray(resultado) ? resultado : (resultado && resultado.data) || [];
        this.etiquetas = lista.map((r) => ({
          value: r.id, label: r.nome, cor: r.cor_referencia || '#6c757d',
        }));
      } catch (_e) { this.etiquetas = []; }
    },
    /**
     * Cria nova etiqueta a partir do termo digitado no Multiselect quando
     * a busca não acha resultado. Adiciona no system + já aplica como
     * filtro ativo no kanban.
     */
    async criarEtiquetaRapida() {
      const nome = (this.termoBuscaEtiqueta || '').trim();
      if (!nome || this.salvandoNova) return;
      this.salvandoNova = true;
      try {
        const { data: nova } = await axios.post('/rotulo', {
          nome,
          cor_referencia: this.corCriacaoRapida,
          ativo: true,
        });
        if (nova && nova.id) {
          // Adiciona à lista de opções
          this.etiquetas.push({
            value: nova.id,
            label: nova.nome,
            cor: nova.cor_referencia || '#6c757d',
          });
          // Aplica como filtro ativo (atendente costuma querer ver
          // imediatamente as conversas que receberam essa etiqueta)
          this.atualizar('rotulosIds', [...(this.filtros.rotulosIds || []), nova.id]);
        }
        this.termoBuscaEtiqueta = '';
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert(`Falha ao criar etiqueta: ${(error.response && error.response.data && error.response.data.error) || error.message}`);
      } finally {
        this.salvandoNova = false;
      }
    },
  },
};
</script>

<template>
  <div class="kanban-filtros d-flex align-items-center gap-2 flex-wrap">
    <div class="filtro-busca">
      <div class="search-box">
        <input
          v-model="busca"
          type="text"
          class="form-control form-control-sm search"
          placeholder="Buscar nome, documento, cidade..."
        />
        <i class="ri-search-line search-icon"></i>
      </div>
    </div>
    <!-- Filtro de UF oculto por enquanto. Pra reativar: remover v-if="false". -->
    <div v-if="false" class="filtro-uf">
      <Multiselect
        v-model="estados"
        :options="ufs"
        mode="tags"
        :searchable="true"
        :close-on-select="false"
        placeholder="UF"
      />
    </div>
    <div class="filtro-status">
      <Multiselect
        v-model="status"
        :options="statusOpcoes"
        label="label"
        value-prop="value"
        :searchable="false"
        :can-clear="false"
        :can-deselect="false"
        placeholder="Status"
      />
    </div>
    <div class="filtro-etiquetas">
      <Multiselect
        v-model="rotulosIds"
        v-model:search="termoBuscaEtiqueta"
        :options="etiquetas"
        mode="tags"
        label="label"
        value-prop="value"
        :searchable="true"
        :close-on-select="false"
        placeholder="Etiquetas"
      >
        <template #tag="{ option, handleTagRemove }">
          <span class="multiselect-tag" :style="{ background: option.cor }">
            {{ option.label }}
            <span class="multiselect-tag-remove" @click.prevent="handleTagRemove(option, $event)">
              <span class="multiselect-tag-remove-icon"></span>
            </span>
          </span>
        </template>
        <template #option="{ option }">
          <span class="badge me-2" :style="{ backgroundColor: option.cor }">&nbsp;</span>
          {{ option.label }}
        </template>
        <template #noresults>
          <div class="criar-rapida-card">
            <div class="small text-muted mb-2">
              Sem resultado para <strong>"{{ termoBuscaEtiqueta }}"</strong>
            </div>
            <div class="d-flex align-items-center gap-1 mb-2">
              <span class="small me-1">Cor:</span>
              <button
                v-for="c in coresDisponiveis"
                :key="c"
                type="button"
                class="cor-swatch-mini"
                :class="{ ativa: corCriacaoRapida === c }"
                :style="{ backgroundColor: c }"
                :title="c"
                @click.stop="corCriacaoRapida = c"
              ></button>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-sm w-100"
              :disabled="salvandoNova"
              @click.stop="criarEtiquetaRapida"
            >
              <i v-if="salvandoNova" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-add-line me-1"></i>
              {{ salvandoNova ? 'Criando...' : `Criar "${termoBuscaEtiqueta}"` }}
            </button>
          </div>
        </template>
      </Multiselect>
    </div>
    <button
      v-if="temFiltrosAtivos"
      type="button"
      class="btn btn-sm btn-outline-secondary"
      title="Limpar filtros"
      @click="resetFiltros"
    >
      <i class="ri-close-line"></i>
    </button>
  </div>
</template>

<style scoped>
/* Altura unificada — px FIXO. O cálculo `1.5em + ...` resolve diferente
   conforme o font-size do contexto (search 0.875rem vs multiselect
   0.8125rem), então fixamos em pixel pra todos baterem na mesma linha
   do form-control-sm Bootstrap (~31px). */
.kanban-filtros {
  --kf-altura: 31px;
  row-gap: 0.5rem;
  align-items: center;
}

.filtro-busca     { width: 300px; }
.filtro-uf        { width: 130px; }
.filtro-status    { width: 170px; }
.filtro-etiquetas { width: 320px; }

/* Search input — ícone à ESQUERDA (padrão do tema Velzon). Sobrepõe
   estilos globais que possam inflar a altura/padding. */
.search-box { position: relative; }
.search-box .search.form-control {
  height: var(--kf-altura);
  min-height: var(--kf-altura);
  padding: 0 0.65rem 0 2rem;
  font-size: 0.8125rem;
  line-height: 1.5;
}
.search-box .search-icon {
  position: absolute;
  left: 0.65rem;
  right: auto;
  top: 50%;
  transform: translateY(-50%);
  color: var(--vz-secondary-color);
  pointer-events: none;
  font-size: 0.95rem;
  line-height: 1;
}

/* ── Multiselect ─────────────────────────────────────────────────────
   Estratégia: travar SÓ a altura do wrapper externo via --ms-min-height
   e deixar o `align-items: center` nativo do Multiselect cuidar do
   alinhamento vertical do placeholder/label/search. Forçar altura nos
   elementos internos quebra o centralizamento. */
.kanban-filtros :deep(.multiselect) {
  /* Trava a altura externa idêntica ao input, sem deixar conteúdo interno
     empurrar acima. box-sizing: border-box garante que a borda de 1px
     conte dentro dos 31px (mesmo do .form-control-sm). */
  height: var(--kf-altura);
  min-height: var(--kf-altura);
  max-height: var(--kf-altura);
  box-sizing: border-box;
  --ms-min-height: var(--kf-altura);
  --ms-py: 0;
  --ms-px: 0.65rem;
  --ms-font-size: 0.8125rem;
  --ms-line-height: 1.5;
  --ms-radius: var(--vz-border-radius);
  --ms-border-color: var(--vz-input-border);
  --ms-bg: var(--vz-input-bg);
  --ms-ring-color: rgba(64, 81, 137, 0.2);
  --ms-ring-width: 0.15rem;
  --ms-tag-py: 0.05rem;
  --ms-tag-px: 0.4rem;
  --ms-tag-font-size: 0.7rem;
}
.kanban-filtros :deep(.multiselect.is-active) { border-color: var(--vz-primary); }

/* O wrapper interno preenche 100% da altura externa travada acima.
   Usa flex pra centralizar verticalmente o conteúdo (placeholder,
   single-label, tags-search) sem precisar setar height neles. */
.kanban-filtros :deep(.multiselect-wrapper) {
  min-height: 100%;
  height: 100%;
  box-sizing: border-box;
}
.kanban-filtros :deep(.multiselect-tags) {
  min-height: 100%;
  height: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
  margin: 0;
  padding: 0 4px;
}
.kanban-filtros :deep(.multiselect-tag) {
  margin: 0 2px;
  line-height: 1.2;
}

/* Criação inline no slot #noresults */
.criar-rapida-card {
  padding: 0.6rem 0.75rem;
  background: var(--vz-tertiary-bg);
}
.cor-swatch-mini {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, border-color 0.1s;
}
.cor-swatch-mini:hover { transform: scale(1.2); }
.cor-swatch-mini.ativa { border-color: var(--vz-body-color); transform: scale(1.2); }

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
